import { ReactNode, useEffect, useMemo, useState } from 'react';
import {
    Alert,
    Box,
    Checkbox,
    FormControlLabel,
    Typography,
} from '@mui/material';
import { useWatch } from 'react-hook-form';
import { CTableWrapper, DDatePicker, IRhTab } from '../../dum';
import { DATE_FORMAT, formatFloat } from '../../../helpers';
import { useProductionPerformanceConsumptionRatesQuery } from '../../../services';
import { HOURLY_DATA_EPOCH } from './window';
import { useRatesCache } from './rates-cache';
import {
    FlagUngradable,
    gradeLine,
    LineGrade,
    lineKey,
    MIN_BASELINE_RUNS,
    RecordedContribution,
    summarizeGrades,
} from './performance-flags';
import {
    PerformanceFlagChips,
    PerformanceFlagMarks,
    UNIT_LABEL,
} from './performance-flag-chips';

/**
 * What the database already holds for the production being edited.
 *
 * The tab grades what the user is TYPING, but the baseline must exclude what
 * was SAVED — those are different numbers the moment anything is edited. See
 * RecordedContribution.
 */
export interface SavedProductionSnapshot {
    // 'YYYY-MM-DD', as the form itself stores it.
    startDate: string;
    waste: number;
    lines: {
        machineId: number;
        productId: number;
        kilos: number;
        groups: number;
        hours: number | null;
    }[];
    // Consumed lines, for self-excluding this production from the machine
    // consumption baseline the way `lines` does for the packed one. Carries the
    // consumed side's own `hours` too: it is the tab's default denominator, so
    // its saved contribution must be subtractable from the baseline just like
    // kilos. (The packed-hours denominator is self-excluded via `lines`.)
    consumedLines: {
        machineId: number;
        kilos: number;
        hours: number | null;
    }[];
}

// Line shape as react-hook-form holds it. Numeric inputs can hand back strings
// mid-edit, so every number is read through toNumber rather than trusted.
interface FormLine {
    kilos?: number | string | null;
    groups?: number | string | null;
    hours?: number | string | null;
    machine?: { id?: number | null; name?: string | null } | null;
    product?: { id?: number | null; compound_description?: string | null } | null;
}

const toNumber = (value: unknown): number => {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
};

const UNGRADABLE_TEXT: Record<FlagUngradable, string> = {
    'pre-epoch': 'Anterior al registro de horas',
    'no-hours': 'Sin horas capturadas',
    'thin-history': `Menos de ${MIN_BASELINE_RUNS} corridas previas`,
    pending: 'Calculando…',
};

// The reason a line could not be judged, taken from whichever axis refused —
// they only ever differ when hours are missing, which merma survives. A line
// that WAS judged can still carry a note: a machine-level baseline is a weaker
// comparison than the product's own history and must never pass silently.
function noteText(line: LineGrade): string | null {
    const reason = line.throughput.ungradable ?? line.merma.ungradable;
    const fallback =
        line.baselineSource === 'machine'
            ? 'Promedio de la máquina (producto sin historial suficiente)'
            : null;
    if (!reason) return fallback;
    const refusal =
        reason === 'thin-history'
            ? `Historial insuficiente (${line.throughput.baselineRuns} corridas)`
            : UNGRADABLE_TEXT[reason];
    return [refusal, fallback].filter(Boolean).join(' · ');
}

const percent = (value: number | null): string =>
    value === null ? '—' : `${formatFloat(value * 100, 1)}%`;

const rate = (value: number | null): string =>
    value === null ? '—' : formatFloat(value);

// ---- consumed (cutting) side ------------------------------------------------
//
// This second table is now exclusively about PRODUCTOS CONSUMIDOS:
//   Rendimiento = kilos consumidos / horas
//
// The two COMBINATION metrics — Eficiencia de corte (kilos empacados / kilos
// consumidos) and Rendimiento de corte real (empacado / horas) — cross the
// packed (type 1) and consumed (type 2) sides, so they move to a separate
// THIRD table that is intentionally a PLACEHOLDER for now (see the panel):
// the numbers depend on capture that is still under discovery with production
// (rolls span/reuse across runs, registered only on mounting, and the consumed
// `hours` column currently just mirrors the packed hours). We build the shell
// now and compute it once the data pipeline is cleaned.
//
// Rendimiento is MACHINE-level: consumed material is recorded per production
// keyed by machine, and its denominator is the machine's (packed-side) hours —
// a KNOWN mis-capture we surface with a note and keep using deliberately, so
// the infrastructure exists before the data is fixed. Two consumed lines on one
// machine carry the SAME rate — repeated per row, with `sharesMachine` noting it.

// One consumed form line, reduced to what the metrics need.
interface ConsumedFormLine {
    kilos?: number | string | null;
    hours?: number | string | null;
    machine?: { id?: number | null; name?: string | null } | null;
    product?: {
        id?: number | null;
        compound_description?: string | null;
    } | null;
}

// The raw machine consumption baseline as the query returns it (self-exclusion
// happens here, in the browser, against the saved snapshot).
export interface MachineConsumptionAggregate {
    consumedKilos: number;
    consumedHours: number;
    packedHours: number;
    runs: number;
}

interface ConsumedRow {
    key: string;
    description: string;
    machineName: string;
    consumedKilos: number; // this line's own consumed kilos
    sharesMachine: boolean; // another consumed line runs on the same machine
    pending: boolean; // the machine baseline has not arrived yet
    // This production's own machine-level rate (repeated across shared lines).
    rendimiento: number | null;
    // The machine baseline, self-excluding this production.
    rendimientoBaseline: number | null;
    baselineRuns: number;
}

const sumBy = <T,>(rows: T[], pick: (row: T) => number): number =>
    rows.reduce((acc, row) => acc + pick(row), 0);

const ratio = (numerator: number, denominator: number): number | null =>
    denominator > 0 ? numerator / denominator : null;

/**
 * Build one row per consumed line. Everything but `consumedKilos` is a
 * machine-level fact, so lines sharing a machine come out identical bar their
 * own consumed kilos and description.
 *
 * `packedLines` carries the (packed-side) hours that stand in as the
 * denominator for THIS production; `saved` is what the machine baseline already
 * counts, subtracted before dividing exactly as the packed flags do — only when
 * the saved production falls inside the window. Consumed kilos and hours come
 * from the consumption query (`consumptionByMachine`, null while it loads).
 */
function computeConsumedRows({
    consumedLines,
    packedLines,
    saved,
    fromDate,
    consumptionByMachine,
    usePackedHours,
}: {
    consumedLines: {
        machineId: number;
        machineName: string;
        description: string;
        kilos: number;
        hours: number;
    }[];
    packedLines: { machineId: number; kilos: number; hours: number | null }[];
    saved?: SavedProductionSnapshot;
    fromDate: string;
    consumptionByMachine: Map<number, MachineConsumptionAggregate> | null;
    // false (default) → divide by the consumed side's own hours (matches the
    // Rendimiento page); true → divide by the packed side's hours, the
    // mis-capture workaround.
    usePackedHours: boolean;
}): ConsumedRow[] {
    // The saved production is inside the machine baseline only if it was saved
    // inside the window; otherwise the aggregate never counted it and nothing is
    // taken back out (mirrors selfExcludedWindow).
    const savedInWindow = !!saved && saved.startDate >= fromDate;

    const machineLineCount = new Map<number, number>();
    consumedLines.forEach((line) =>
        machineLineCount.set(
            line.machineId,
            (machineLineCount.get(line.machineId) ?? 0) + 1,
        ),
    );

    return consumedLines.map((line, index) => {
        const m = line.machineId;
        const onMachine = <T extends { machineId: number }>(rows: T[]): T[] =>
            rows.filter((row) => row.machineId === m);

        // This production's own, machine-level. Hours come from whichever side
        // the denominator toggle selects — consumed (default) or packed.
        const ownConsumed = sumBy(onMachine(consumedLines), (row) => row.kilos);
        const ownHours = usePackedHours
            ? sumBy(onMachine(packedLines), (row) => row.hours ?? 0)
            : sumBy(onMachine(consumedLines), (row) => row.hours);

        // What the machine baseline already holds for this production — subtract
        // the same side's saved hours the denominator uses.
        const savedConsumed = savedInWindow
            ? sumBy(onMachine(saved!.consumedLines), (row) => row.kilos)
            : 0;
        const savedHours = savedInWindow
            ? usePackedHours
                ? sumBy(onMachine(saved!.lines), (row) => row.hours ?? 0)
                : sumBy(onMachine(saved!.consumedLines), (row) => row.hours ?? 0)
            : 0;

        const hist = consumptionByMachine?.get(m);
        const pending = consumptionByMachine === null;

        // Self-excluded machine baseline. The hours numerator switches with the
        // toggle: the consumed side's own hours by default, the packed side's
        // when the workaround is on.
        const baseConsumed = Math.max(0, (hist?.consumedKilos ?? 0) - savedConsumed);
        const histHours = usePackedHours
            ? hist?.packedHours ?? 0
            : hist?.consumedHours ?? 0;
        const baseHours = Math.max(0, histHours - savedHours);
        const baseRuns = Math.max(
            0,
            (hist?.runs ?? 0) - (savedInWindow && savedConsumed > 0 ? 1 : 0),
        );

        return {
            key: `${line.machineId}-${line.description}-${index}`,
            description: line.description,
            machineName: line.machineName,
            consumedKilos: line.kilos,
            sharesMachine: (machineLineCount.get(m) ?? 0) > 1,
            pending,
            rendimiento: ratio(ownConsumed, ownHours),
            rendimientoBaseline: baseRuns > 0 ? ratio(baseConsumed, baseHours) : null,
            baselineRuns: baseRuns,
        };
    });
}

// Section heading shared by the three tables — a bold label with a primary
// accent bar so each table reads as its own clearly delimited section.
function SectionTitle({ children }: { children: ReactNode }) {
    return (
        <Typography
            variant={'subtitle1'}
            sx={{
                fontWeight: 700,
                lineHeight: 1.3,
                pl: 1,
                borderLeft: 3,
                borderColor: 'primary.main',
            }}
        >
            {children}
        </Typography>
    );
}

// Horizontal breathing room for the dense first table: its header cells have
// zero padding and body cells only 4px, so adjacent column names read as one.
// Wrapping both the title and the content in the same gutter opens a clear gap
// while keeping a right-aligned column's value under its heading.
function Gutter({ children }: { children: ReactNode }) {
    return (
        <Box component={'span'} sx={{ display: 'block', px: 1 }}>
            {children}
        </Box>
    );
}

interface IProductionPerformancePanel {
    saved?: SavedProductionSnapshot;
}

/**
 * The Rendimiento tab of the production upsert form.
 *
 * Renders inside the react-hook-form provider, so it grades the values as they
 * are typed — which is the point. The same flag on the Producción list can only
 * tell you a run went badly after the fact; here it can still be acted on,
 * whether that means fixing a mistyped hours field or looking again at the run
 * itself before it is filed.
 */
export function ProductionPerformancePanel({
    saved,
}: IProductionPerformancePanel) {
    const formLines = useWatch({ name: 'order_production_products' }) as
        | FormLine[]
        | undefined;
    const waste = useWatch({ name: 'waste' });
    const startDate = useWatch({ name: 'start_date' }) as string | undefined;

    // The tab's own "Desde" — the lower bound of the baseline window, local to
    // this tab and defaulting to the epoch. It does NOT touch the Producción
    // list chips or the planning board: those read the rate cache under the
    // default epoch key, and this window keys its own cache entries by the date
    // (see useRatesCache). It flows to two places that must agree on it — the
    // aggregate fetch and gradeLine's windowFrom — or a run would be graded
    // against a window it was never counted in.
    const [fromDate, setFromDate] = useState<string>(HOURLY_DATA_EPOCH);

    // Verification aid: normally the baseline self-excludes this production (its
    // saved contribution is subtracted before dividing), which is what makes the
    // Prom. columns match the dialog's self-excluded KPI. Checking this keeps the
    // run IN the baseline, so the numbers instead line up with a surface that
    // does not self-exclude — the Rendimiento page headline. Only has an effect
    // on an already-saved production; a new one is not in the aggregate to begin
    // with.
    const [includeCurrentRun, setIncludeCurrentRun] = useState<boolean>(false);

    // Consumed-table denominator. Off by default → divide by the consumed side's
    // own hours (agrees with the Rendimiento page's Consumo kg/hr). On → divide
    // by the packed side's hours, a workaround for the consumed-hours mis-capture.
    const [usePackedHours, setUsePackedHours] = useState<boolean>(false);

    // Packed grading only: when on, the first table's Prom. columns are built
    // from single-product runs (product_count === 1). Multi-product runs capture
    // hours on their first packed line only, so their per-product kg/hr is
    // unrecoverable — this restricts the baseline to clean runs. It does NOT
    // touch the consumed table (no hours flaw there, per the capture notes).
    const [singleProductOnly, setSingleProductOnly] = useState<boolean>(false);

    const { ensure, get } = useRatesCache();

    // Only complete lines can be graded; an in-progress row with no product
    // picked yet is not an error, it is just not ready.
    const lines = useMemo(
        () =>
            (formLines ?? []).flatMap((line) => {
                const machineId = line.machine?.id;
                const productId = line.product?.id;
                if (!machineId || !productId) return [];
                return [
                    {
                        machineId,
                        productId,
                        description: line.product?.compound_description ?? '',
                        kilos: toNumber(line.kilos),
                        groups: toNumber(line.groups),
                        hours:
                            line.hours === null || line.hours === undefined
                                ? null
                                : toNumber(line.hours),
                    },
                ];
            }),
        [formLines],
    );

    useEffect(() => {
        if (lines.length === 0) return;
        ensure(
            lines.flatMap((line) => [
                { machineId: line.machineId, productId: line.productId },
                { machineId: line.machineId, productId: null },
            ]),
            fromDate,
            singleProductOnly,
        );
    }, [lines, ensure, fromDate, singleProductOnly]);

    // What the aggregate already counts for this production, keyed by combo. A
    // line the user just added is absent, and correctly contributes nothing.
    const recordedByLine = useMemo(() => {
        const map = new Map<string, RecordedContribution>();
        if (!saved) return map;
        const savedTotal = saved.lines.reduce((acc, l) => acc + l.kilos, 0);
        saved.lines.forEach((line) => {
            map.set(lineKey(line.productId, line.machineId), {
                startDate: saved.startDate,
                kilos: line.kilos,
                groups: line.groups,
                hours: line.hours,
                wasteShare:
                    savedTotal > 0
                        ? saved.waste * (line.kilos / savedTotal)
                        : 0,
            });
        });
        return map;
    }, [saved]);

    const graded = useMemo(() => {
        const totalKilos = lines.reduce((acc, line) => acc + line.kilos, 0);
        const wasteTotal = toNumber(waste);
        return lines.map((line) => ({
            ...line,
            grade: gradeLine({
                machineId: line.machineId,
                productId: line.productId,
                startDate: startDate || '',
                kilos: line.kilos,
                groups: line.groups,
                hours: line.hours,
                wasteShare:
                    totalKilos > 0
                        ? wasteTotal * (line.kilos / totalKilos)
                        : 0,
                // A production with no id has never been aggregated, so there
                // is nothing to take back out. An edit subtracts what was
                // SAVED, never what is currently on screen — unless the
                // "include this run" toggle is on, which keeps it in.
                recorded: includeCurrentRun
                    ? null
                    : recordedByLine.get(
                          lineKey(line.productId, line.machineId),
                      ) ?? null,
                rate: get(
                    line.machineId,
                    line.productId,
                    fromDate,
                    singleProductOnly,
                ),
                machineRate: get(
                    line.machineId,
                    null,
                    fromDate,
                    singleProductOnly,
                ),
                windowFrom: fromDate,
            }),
        }));
    }, [
        lines,
        waste,
        startDate,
        recordedByLine,
        get,
        fromDate,
        includeCurrentRun,
        singleProductOnly,
    ]);

    const summary = useMemo(
        () => summarizeGrades(graded.map((row) => row.grade)),
        [graded],
    );

    // ---- consumed (cutting) side ----------------------------------------
    const consumedFormLines = useWatch({
        name: 'order_production_products_consumed',
    }) as ConsumedFormLine[] | undefined;

    const consumedLines = useMemo(
        () =>
            (consumedFormLines ?? []).flatMap((line) => {
                const machineId = line.machine?.id;
                if (!machineId) return [];
                return [
                    {
                        machineId,
                        machineName: line.machine?.name ?? '',
                        description: line.product?.compound_description ?? '',
                        kilos: toNumber(line.kilos),
                        hours: toNumber(line.hours),
                    },
                ];
            }),
        [consumedFormLines],
    );

    const consumedMachineIds = useMemo(
        () => Array.from(new Set(consumedLines.map((line) => line.machineId))),
        [consumedLines],
    );

    const { data: consumptionData } =
        useProductionPerformanceConsumptionRatesQuery({
            variables: { machineIds: consumedMachineIds, fromDate },
            skip: consumedMachineIds.length === 0,
            fetchPolicy: 'cache-first',
        });

    // null until the query has answered, so the rows can show "Calculando…"
    // rather than an empty baseline. The query now returns one row per (machine,
    // consumed product); the machine-level Rendimiento sums consumed kilos across
    // a machine's materials, while packed_hours/runs are machine-level and
    // repeated, so the first row per machine carries them. (The per-material
    // consumed_kilos is available on the rows for later use — see the placeholder
    // combination table — but the visible rate stays machine-level for now.)
    const consumptionByMachine = useMemo(() => {
        const rows = consumptionData?.getMachineConsumptionRates;
        if (!rows) return null;
        const map = new Map<number, MachineConsumptionAggregate>();
        rows.forEach((row) => {
            const existing = map.get(row.machine_id);
            if (existing) {
                existing.consumedKilos += row.consumed_kilos;
            } else {
                map.set(row.machine_id, {
                    consumedKilos: row.consumed_kilos,
                    consumedHours: row.consumed_hours,
                    packedHours: row.packed_hours,
                    runs: row.runs,
                });
            }
        });
        return map;
    }, [consumptionData]);

    const consumedRows = useMemo(
        () =>
            computeConsumedRows({
                consumedLines,
                packedLines: lines,
                // Withholding the snapshot keeps this run in the machine
                // baseline — the consumed-side counterpart to the packed toggle.
                saved: includeCurrentRun ? undefined : saved,
                fromDate,
                consumptionByMachine,
                usePackedHours,
            }),
        [
            consumedLines,
            lines,
            saved,
            fromDate,
            consumptionByMachine,
            includeCurrentRun,
            usePackedHours,
        ],
    );

    if (lines.length === 0) {
        return (
            <Box sx={{ py: 2 }}>
                <Alert severity={'info'}>
                    Agrega al menos un producto con máquina para evaluar el
                    rendimiento de esta producción.
                </Alert>
            </Box>
        );
    }

    return (
        <Box sx={{ py: 1 }}>
            {/* Controls, pinned top-left: the baseline window and the
                self-exclusion toggle drive every table below. */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 2,
                    mb: 2,
                }}
            >
                <Box sx={{ width: 160 }}>
                    <DDatePicker
                        views={['day']}
                        inputFormat={DATE_FORMAT}
                        label={'Desde'}
                        value={fromDate}
                        clearable={false}
                        disableGutters
                        onChange={(val) => {
                            if (val) setFromDate(val);
                        }}
                    />
                </Box>
                <FormControlLabel
                    sx={{ mr: 0 }}
                    control={
                        <Checkbox
                            size={'small'}
                            checked={includeCurrentRun}
                            onChange={(e) =>
                                setIncludeCurrentRun(e.target.checked)
                            }
                        />
                    }
                    label={
                        <Typography variant={'caption'}>
                            Incluir esta corrida en el promedio
                        </Typography>
                    }
                />
                <FormControlLabel
                    sx={{ mr: 0 }}
                    control={
                        <Checkbox
                            size={'small'}
                            checked={singleProductOnly}
                            onChange={(e) =>
                                setSingleProductOnly(e.target.checked)
                            }
                        />
                    }
                    label={
                        <Typography variant={'caption'}>
                            Solo producciones de un solo producto
                        </Typography>
                    }
                />
            </Box>

            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    gap: 1,
                    mb: 1,
                }}
            >
                <SectionTitle>
                    Comparado con el histórico de cada producto · máquina
                </SectionTitle>
                <PerformanceFlagChips grade={summary} />
            </Box>

            <CTableWrapper
                padding={false}
                items={[
                    {
                        title: <Gutter>Producto</Gutter>,
                        cellGrow: 26,
                        content: (row) => (
                            <Gutter>
                                {row.description}
                                <PerformanceFlagMarks line={row.grade} />
                            </Gutter>
                        ),
                    },
                    // Both units, always, even though only one of them is
                    // graded. Bultos are what the floor counts and kilos are
                    // what everything downstream is priced in — showing only
                    // the graded one leaves half the room unable to sanity-check
                    // the number.
                    {
                        title: <Gutter>bultos/hr</Gutter>,
                        cellGrow: 10,
                        textAlign: 'right',
                        content: (row) => (
                            <Gutter>{rate(row.grade.throughput.bultos.value)}</Gutter>
                        ),
                    },
                    {
                        title: <Gutter>Prom. bultos/hr</Gutter>,
                        cellGrow: 12,
                        textAlign: 'right',
                        content: (row) => (
                            <Gutter>
                                {rate(row.grade.throughput.bultos.baseline)}
                            </Gutter>
                        ),
                    },
                    {
                        title: <Gutter>kg/hr</Gutter>,
                        cellGrow: 10,
                        textAlign: 'right',
                        content: (row) => (
                            <Gutter>{rate(row.grade.throughput.kilos.value)}</Gutter>
                        ),
                    },
                    {
                        title: <Gutter>Prom. kg/hr</Gutter>,
                        cellGrow: 11,
                        textAlign: 'right',
                        content: (row) => (
                            <Gutter>
                                {rate(row.grade.throughput.kilos.baseline)}
                            </Gutter>
                        ),
                    },
                    {
                        title: <Gutter>Índice</Gutter>,
                        cellGrow: 12,
                        textAlign: 'right',
                        content: (row) => (
                            <Gutter>
                                {percent(row.grade.throughput.ratio)}
                                {row.grade.throughput.ratio === null ? null : (
                                    <Typography
                                        variant={'caption'}
                                        color={'text.secondary'}
                                        sx={{ display: 'block' }}
                                    >
                                        {UNIT_LABEL[row.grade.throughput.unit]}
                                    </Typography>
                                )}
                            </Gutter>
                        ),
                    },
                    {
                        title: <Gutter>Merma</Gutter>,
                        cellGrow: 9,
                        textAlign: 'right',
                        content: (row) => (
                            <Gutter>{percent(row.grade.merma.value)}</Gutter>
                        ),
                    },
                    {
                        title: <Gutter>Merma prom.</Gutter>,
                        cellGrow: 11,
                        textAlign: 'right',
                        content: (row) => (
                            <Gutter>{percent(row.grade.merma.baseline)}</Gutter>
                        ),
                    },
                    {
                        title: <Gutter>Corridas</Gutter>,
                        cellGrow: 9,
                        textAlign: 'right',
                        content: (row) => (
                            <Gutter>
                                {row.grade.throughput.baselineRuns > 0
                                    ? row.grade.throughput.baselineRuns
                                    : '—'}
                            </Gutter>
                        ),
                    },
                    {
                        title: <Gutter>Nota</Gutter>,
                        cellGrow: 20,
                        content: (row) => <Gutter>{noteText(row.grade)}</Gutter>,
                    },
                ]}
                data={graded}
            />

            <Typography
                variant={'caption'}
                color={'text.secondary'}
                sx={{ display: 'block', mt: 1 }}
            >
                El promedio de cada línea se calcula sobre las corridas desde la
                fecha «Desde» (por defecto, el inicio del registro de horas),{' '}
                {includeCurrentRun
                    ? 'incluye esta misma producción (para comparar con la página de Rendimiento)'
                    : 'excluye esta misma producción'}
                , y requiere al menos {MIN_BASELINE_RUNS} corridas previas; si el
                producto no las tiene
                en esta máquina, se compara contra el promedio de la máquina y la
                columna Nota lo indica. El Índice se
                califica en bultos/hr cuando el producto se registra en bultos, y
                en kg/hr cuando no. Una línea sin marca no fue calificada como
                buena: puede no haber sido calificable — la columna Nota lo
                indica.
            </Typography>

            {consumedRows.length > 0 ? (
                <Box sx={{ mt: 3 }}>
                    <Box
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            flexWrap: 'wrap',
                            gap: 1,
                            mb: 1,
                        }}
                    >
                        <SectionTitle>
                            Productos consumidos (por máquina)
                        </SectionTitle>
                        <FormControlLabel
                            sx={{ ml: 'auto', mr: 0 }}
                            control={
                                <Checkbox
                                    size={'small'}
                                    checked={usePackedHours}
                                    onChange={(e) =>
                                        setUsePackedHours(e.target.checked)
                                    }
                                />
                            }
                            label={
                                <Typography variant={'caption'}>
                                    Usar horas empacadas (evita la mala captura)
                                </Typography>
                            }
                        />
                    </Box>

                    <CTableWrapper
                        padding={false}
                        items={[
                            {
                                title: 'Material',
                                cellGrow: 24,
                                content: (row) => (
                                    <>
                                        {row.description || '—'}
                                        {row.machineName ? (
                                            <Typography
                                                variant={'caption'}
                                                color={'text.secondary'}
                                                sx={{ display: 'block' }}
                                            >
                                                {row.machineName}
                                            </Typography>
                                        ) : null}
                                    </>
                                ),
                            },
                            {
                                title: 'Consumido',
                                cellGrow: 11,
                                textAlign: 'right',
                                content: (row) => formatFloat(row.consumedKilos),
                            },
                            // Rendimiento = kilos consumidos / horas.
                            {
                                title: 'Rendimiento',
                                cellGrow: 11,
                                textAlign: 'right',
                                content: (row) => rate(row.rendimiento),
                            },
                            {
                                title: 'Prom.',
                                cellGrow: 12,
                                textAlign: 'right',
                                content: (row) =>
                                    row.pending
                                        ? '…'
                                        : rate(row.rendimientoBaseline),
                            },
                            {
                                // Header and body share the same left gutter so
                                // the whole column shifts right as one: the body
                                // cells are locked to a 4px gutter, leaving this
                                // left-aligned "Nota" almost touching the
                                // right-aligned "Prom." — enough that the two
                                // titles read as a single word. Indent both to
                                // open a clear gap while keeping them aligned.
                                title: <Box sx={{ pl: 2.5 }}>Nota</Box>,
                                cellGrow: 20,
                                content: (row) => (
                                    <Box component={'span'} sx={{ pl: 2.5 }}>
                                        {row.pending
                                            ? 'Calculando…'
                                            : [
                                                  row.sharesMachine
                                                      ? 'Comparte máquina'
                                                      : null,
                                                  row.baselineRuns > 0
                                                      ? `${row.baselineRuns} corridas`
                                                      : 'Sin historial',
                                              ]
                                                  .filter(Boolean)
                                                  .join(' · ')}
                                    </Box>
                                ),
                            },
                        ]}
                        data={consumedRows}
                    />

                    <Alert severity={'warning'} sx={{ mt: 1 }}>
                        ⚠️ Las horas del lado consumido están mal capturadas.{' '}
                        {usePackedHours
                            ? 'Con «Usar horas empacadas» activo, Rendimiento divide por las horas del lado empacado para sortear esa mala captura.'
                            : 'Por defecto Rendimiento divide por esas horas del lado consumido (coincide con «Consumo kg/hr» de la página de Rendimiento); activa «Usar horas empacadas» para sortear la mala captura.'}{' '}
                        Se muestra a nivel de máquina, no por producto: el material
                        se registra por máquina al montar el rollo — los rollos se
                        reutilizan entre corridas y no se marca si se consumieron
                        por completo. Es infraestructura: primero se arregla el
                        pipeline de datos; entonces las horas del lado consumido
                        serán confiables (y Rendimiento por producto será posible).
                        En discusión con producción.
                    </Alert>

                    <Typography
                        variant={'caption'}
                        color={'text.secondary'}
                        sx={{ display: 'block', mt: 1 }}
                    >
                        Rendimiento = kilos consumidos ÷{' '}
                        {usePackedHours
                            ? 'horas empacadas'
                            : 'horas consumidas'}
                        . Es cifra de máquina: el material consumido se registra
                        por máquina, no por producto, así que dos líneas en la
                        misma máquina comparten el mismo cálculo (la columna Nota
                        lo indica). El promedio es el histórico de la máquina desde
                        «Desde»,{' '}
                        {includeCurrentRun
                            ? 'incluyendo'
                            : 'excluyendo'}{' '}
                        esta producción.
                    </Typography>

                    {/* THIRD table — combination of packed + consumed.
                        Placeholder only: Eficiencia de corte and Rendimiento de
                        corte real cross type-1 (empacado) and type-2 (consumido)
                        and lean on the same capture still under discovery, so the
                        shell is here but the numbers are deliberately not computed
                        yet (mix machine packed kilos from getMachineProductRates
                        with consumed kilos/hours here once capture is settled). */}
                    <Box sx={{ mt: 3 }}>
                        <Box sx={{ mb: 1 }}>
                            <SectionTitle>
                                Corte y consumo combinado (empacado vs consumido)
                            </SectionTitle>
                        </Box>
                        <Alert severity={'info'}>
                            Pendiente. Eficiencia de corte (kg empacados / kg
                            consumidos) y Rendimiento de corte real (empacado /
                            horas) se calcularán aquí una vez acordada la captura
                            con producción y limpiado el pipeline de datos.
                        </Alert>
                    </Box>
                </Box>
            ) : null}
        </Box>
    );
}

/**
 * The tab descriptor, shaped like useHistorialTab's so it spreads straight into
 * `tabs={[...]}`. Read-only, so `optional` keeps it out of the progress counter.
 */
export function performanceTab(saved?: SavedProductionSnapshot): IRhTab[] {
    return [
        {
            label: 'Rendimiento',
            optional: true,
            fields: [],
            content: <ProductionPerformancePanel saved={saved} />,
            // Nothing to grade until a product line exists.
            disabled: (values) =>
                !(values.order_production_products as FormLine[] | undefined)
                    ?.length,
        },
    ];
}
